import { Box } from "@stenajs-webui/core";
import { PrimaryButton } from "@stenajs-webui/elements";
import { ModalFooter } from "@stenajs-webui/modal";

import {
  PayButton,
  SeePaymentLink,
} from "../../../../../common-ui/payment/PayButton";
import { useHasPermission } from "../../../../../common/hooks/useHasPermission";
import { useShowCalculatePrice } from "../../../../../common/pricing/useShowCalculatePrice";
import type { TrackerCategory } from "../../../../../common/tracking/trackerService";
import { transformToUpdateBookingFormState } from "../../../../../common/update-information/utils/transformToUpdateBookingFormState";
import { PRINT_HIDE_CLASSNAME } from "../../../../../globalStyles";
import type { BasicBookingFragment } from "../../../../../gql/graphql";
import { Permission } from "../../../../../services/authentication/utils/authUtils";
import { isBookingEditable } from "../../../../../services/update-form/utils/bookingUtils";
import { CalculatePriceForDetails } from "../../pricing/CalculatePriceForDetails";
import {
  bookingHasPayments,
  bookingIsPayable,
} from "../../utils/showPaymentUtil";
import { BookingCancelButton } from "../BookingCancelButton";

const trackerCategory: TrackerCategory = "Details";

interface BookingDetailsViewFooterProps {
  booking: BasicBookingFragment;
  onCancelFail: () => void;
  onClickEdit: () => void;
}

export function BookingDetailsViewFooter({
  booking,
  onCancelFail,
  onClickEdit,
}: BookingDetailsViewFooterProps) {
  const showCalculatePrice = useShowCalculatePrice(
    booking.customer.isCashCustomer,
    booking.bookingStatus.status,
  );

  const isEditable = isBookingEditable(booking);
  const showSeePaymentLink = bookingHasPayments(booking);
  const showPayButton = bookingIsPayable(booking);
  const getHasPermission = useHasPermission();
  const hasBookingWritePermission = getHasPermission(Permission.EditBookings);
  const showEditButton = isEditable && hasBookingWritePermission;
  const showCancelButton =
    isEditable && hasBookingWritePermission && booking.cancelable;

  if (
    !showSeePaymentLink &&
    !showEditButton &&
    !showCancelButton &&
    !showPayButton
  ) {
    return null;
  }

  return (
    <ModalFooter sticky className={PRINT_HIDE_CLASSNAME}>
      <Box
        justifyContent={"space-between"}
        flexDirection={["column", null, "row"]}
        flex={1}
        gap
      >
        {showCancelButton && (
          <BookingCancelButton booking={booking} onFail={onCancelFail} />
        )}
        <Box gap={1} flexDirection={["column", null, "row"]}>
          {showSeePaymentLink && (
            <SeePaymentLink
              bookingNo={booking.bookingNo}
              trackerCategory={trackerCategory}
            />
          )}
          {showCalculatePrice && (
            <CalculatePriceForDetails
              formState={transformToUpdateBookingFormState(booking, {
                canBeEmpty: true,
              })}
              placement={"top"}
            />
          )}
          {showPayButton && (
            <PayButton
              secondary
              bookingNo={booking.bookingNo}
              trackerCategory={trackerCategory}
            />
          )}
          {showEditButton && (
            <PrimaryButton
              label={"Edit booking"}
              onClick={onClickEdit}
              title={"Edit booking"}
            />
          )}
        </Box>
      </Box>
    </ModalFooter>
  );
}
